import React, { useState, useEffect } from 'react';
import avatar from '../assets/images/avatar.png';
import '../pages/Journal.css';
import { Helmet } from 'react-helmet';
import { 
  updateBlogPostStats, 
  formatNumber,
  saveLikesToJSON,
  loadLikesFromLocalStorage,
  saveLikesToLocalStorage
} from '../utils/blogUtils';
import { trackBlogInteraction, trackButtonClick } from '../utils/analyticsUtils';
import blogPostsData from '../data/blogPosts.json';

function Journal() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    // Load blog posts directly from imported JSON
    setBlogPosts(blogPostsData.posts || []);
    
    // Load liked posts from localStorage
    const savedLikes = loadLikesFromLocalStorage();
    setLikedPosts(savedLikes);
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleLike = async (postId) => {
    if (!likedPosts.has(postId) && !isSaving) {
      setIsSaving(true);
      
      try {
        // Track the like interaction
        const post = blogPosts.find(p => p.id === postId);
        trackBlogInteraction(postId, 'like', post?.title || '');
        
        // Update the posts with new like count
        const updatedPosts = updateBlogPostStats(blogPosts, postId, 'like');
        setBlogPosts(updatedPosts);
        
        // Update liked posts state
        const newLikedPosts = new Set([...likedPosts, postId]);
        setLikedPosts(newLikedPosts);
        
        // Save to localStorage
        saveLikesToLocalStorage(newLikedPosts);
        
        // Save updated JSON file
        await saveLikesToJSON(updatedPosts, postId);
        
        // Show success notification
        showNotification('Like saved!', 'success');
      } catch (error) {
        console.error('Error saving like:', error);
        showNotification('Error saving like. Please try again.', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleView = (postId) => {
    // Track the view interaction
    const post = blogPosts.find(p => p.id === postId);
    trackBlogInteraction(postId, 'view', post?.title || '');
    
    const updatedPosts = updateBlogPostStats(blogPosts, postId, 'view');
    setBlogPosts(updatedPosts);
  };

  const handleReadMoreClick = (e, postId, postTitle) => {
    e.stopPropagation();
    trackButtonClick(`ReadMore-${postId}`, 'Blog');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 Day Ago';
    if (diffDays < 7) return `${diffDays} Days Ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} Weeks Ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} Months Ago`;
    return `${Math.floor(diffDays / 365)} Years Ago`;
  };

  return (
    <div className='journal-contaier'>
    <Helmet>
        <title>Aadhaar Koul | Blog</title>
    </Helmet>
      
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
    <div className='discount'>
      <a href="/shop"><p>Get upto 75% off on my online Store →</p></a>
    </div>
      
      <div className='userid'>
        <div className='Avatar'>
          <img src={avatar} alt="Avatar" />
        </div>
        <div className='online-icon'></div>
        <div className='avatar-text'><h4>Aadhaar Koul</h4></div>
      </div>

      <div className='text1'><h4>Latest Blog Posts...</h4></div>

      {/* Blog Posts Display */}
      <div className='blog-posts-container'>
        {blogPosts.length === 0 ? (
          <div className='no-posts'>
            <h3>No blog posts yet. Add your first post!</h3>
          </div>
        ) : (
          <div className='blog-grid'>
            {blogPosts.map((post) => (
              <div key={post.id} className='blog-card' onClick={() => handleView(post.id)}>
                <div className='blog-card-header'>
                  <h2 className='blog-title'>{post.title}</h2>
                  <span className='blog-date'>{formatDate(post.date)}</span>
                </div>
                <div className='blog-card-content'>
                  <p className='blog-text'>{post.text}</p>
                </div>
                <div className='blog-card-footer'>
                  <div className='blog-stats'>
                    <button 
                      className={`like-btn ${likedPosts.has(post.id) ? 'liked' : ''} ${isSaving ? 'saving' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLike(post.id);
                      }}
                      disabled={isSaving}
                    >
                      <span className='like-icon'>❤</span>
                      <span className='like-count'>{formatNumber(post.likes)}</span>
                      {isSaving && <span className='saving-indicator'>...</span>}
                    </button>
                    <div className='view-count'>
                      <span className='view-icon'>👁</span>
                      <span>{formatNumber(post.views)}</span>
                    </div>
                  </div>
                  <a 
                    href={post.linkUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className='read-more-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReadMoreClick(e, post.id, post.title);
                    }}
                  >
                    Read Full Article →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Journal;
